import { h } from '@omni-torn/ui';
import {
  ARMORY_ACTIONS,
  armoryByItem,
  armoryByMember,
  bankGiveLink,
  FACTION_HISTORY_TERMS,
  fundsByMember,
  type ArmoryAction,
  type ArmoryLine,
  type ChainReport,
  type FactionBankAnswer,
  type FactionOpsAnswer,
  type FactionOpsPart,
  type FundsLine,
  type OpsPerson,
} from '@omni-torn/shared';
import { server } from '../session';
import { csvButton, filterSelect } from '../log-kit/ui';
import { ago, box, duration, ext, facts, kids, loading, money, num, problem, profileUrl, table, tct } from '../ui';

/**
 * Faction ops I (website roadmap S19): Faction → Armory (history under what the bot keeps stocked), Chains (every chain,
 * its report with the bonus hits), Bank (balances, vault requests from Discord with Torn's own Give form for bankers, the
 * money moves) and Raids (raids, territory wars, territory held). From the server's /api/faction/ops*, which reads your
 * faction with the keys members gave the Discord bot — kept beyond an hour only from terms version 9.
 *
 * The faction page draws itself again every 20 s: each part here is built once per visit (kept below) and handed back,
 * so its filters and what it read stay.
 */

const built = new Map<string, HTMLElement>();
/** A new visit to the Faction page starts these afresh. */
export function resetOps(): void {
  built.clear();
}
const once = (key: string, make: () => HTMLElement): HTMLElement => {
  let el = built.get(key);
  if (!el) built.set(key, (el = make()));
  return el;
};

const RANGES: ReadonlyArray<readonly [string, string]> = [
  ['7', 'Last 7 days'],
  ['30', 'Last 30 days'],
  ['90', 'Last 90 days'],
  ['400', 'Everything kept'],
];

const ACTION_LABEL: Record<ArmoryAction, string> = { used: 'Used', filled: 'Filled', loaned: 'Loaned', returned: 'Returned', retrieved: 'Taken back', gave: 'Given', deposited: 'Deposited', other: 'Other' };

const who = (p: OpsPerson | null, names: Map<number, string>) => (p ? (p.id ? ext(profileUrl(p.id), names.get(p.id) ?? p.name ?? `#${p.id}`) : h('span', {}, p.name)) : h('span', { class: 'muted' }, '—'));
const whoText = (p: OpsPerson | null, names: Map<number, string>) => (p ? (p.id ? `${names.get(p.id) ?? p.name} [${p.id}]` : p.name) : '');
const when = (sec: number) => h('span', { title: `${tct(sec * 1000)} TCT` }, ago(sec * 1000));
const keptNote = (a: FactionOpsAnswer) =>
  h(
    'p',
    { class: 'muted small', 'data-ops': 'kept' },
    `${a.readAt ? `Read from Torn ${ago(a.readAt)} with a key a member gave the Discord bot. ` : ''}`,
    a.kept
      ? a.keepDays === 0
        ? 'Kept by Omni-Torn for your faction, with no end date.'
        : `Kept by Omni-Torn for your faction (up to ${num(a.keepDays)} days).`
      : `Only the latest Torn gives (kept an hour). Omni-Torn keeps the history once a member whose key the bot uses agrees to terms version ${FACTION_HISTORY_TERMS}.`,
  );

/** One part from the server, drawn by `draw` into a box; a range picker re-reads with more days. */
function opsPanel(part: FactionOpsPart, title: string, id: string, draw: (a: FactionOpsAnswer, holder: HTMLElement) => void, withRange = true): HTMLElement {
  const holder = h('div', {}, loading('Reading…'));
  let days = '30';
  const read = () => {
    holder.replaceChildren(loading('Reading…'));
    server<FactionOpsAnswer>('GET', `/api/faction/ops?part=${part}${withRange ? `&days=${days}` : ''}`)
      .then((a) => {
        holder.replaceChildren();
        if (a.problem) holder.append(h('p', { class: 'notice', 'data-ops': 'problem' }, a.problem));
        draw(a, holder);
        holder.append(keptNote(a));
      })
      .catch((err) => holder.replaceChildren(problem(err)));
  };
  const extra = withRange
    ? filterSelect('How far back', RANGES, days, (v) => {
        days = v;
        read();
      }, `${id}-days`)
    : null;
  read();
  return box(title, holder, { id, extra });
}

// ---------------------------------------------------------------- armory

export function armoryHistory(names: Map<number, string>): HTMLElement {
  return once('armory', () =>
    opsPanel('armory', 'Armory history', 'armory-history', (a, holder) => {
      const lines = a.armory ?? [];
      let action = '';
      let member = '';
      let item = '';
      const out = h('div', {});
      const members = [...new Map(lines.filter((l) => l.who).map((l) => [String(l.who!.id || l.who!.name), whoText(l.who, names)])).entries()].sort((x, y) => x[1].localeCompare(y[1]));
      const items = [...new Set(lines.map((l) => l.item).filter((x): x is string => !!x))].sort();
      const pick = (l: ArmoryLine) => (!action || l.action === action) && (!member || String(l.who?.id || l.who?.name) === member || String(l.to?.id || l.to?.name) === member) && (!item || l.item === item);
      const draw = () => {
        const list = lines.filter(pick);
        const byItem = armoryByItem(list);
        const byMember = armoryByMember(list);
        out.replaceChildren(
          h('p', { class: 'muted small', 'data-ops': 'armory-count' }, `${num(list.length)} armory line${list.length === 1 ? '' : 's'} · ${num(byItem.length)} item${byItem.length === 1 ? '' : 's'} · ${num(byMember.length)} member${byMember.length === 1 ? '' : 's'}`),
          h('h3', {}, 'By item'),
          table(
            byItem,
            [
              { label: 'Item', get: (r) => r.item, sort: (r) => r.item },
              { label: 'Used', get: (r) => num(r.counts.used ?? 0), sort: (r) => r.counts.used ?? 0, cls: 'r' },
              { label: 'Filled', get: (r) => num(r.counts.filled ?? 0), sort: (r) => r.counts.filled ?? 0, cls: 'r' },
              { label: 'Loaned', get: (r) => num(r.counts.loaned ?? 0), sort: (r) => r.counts.loaned ?? 0, cls: 'r' },
              { label: 'Deposited', get: (r) => num(r.counts.deposited ?? 0), sort: (r) => r.counts.deposited ?? 0, cls: 'r' },
            ],
            { initial: 1, remember: 'ops-armory-items', empty: 'Nothing in these days.' },
          ),
          h('h3', {}, 'By member'),
          table(
            byMember,
            [
              { label: 'Member', get: (r) => who(r.who, names), sort: (r) => whoText(r.who, names).toLowerCase() },
              { label: 'Used', get: (r) => num(r.counts.used ?? 0), sort: (r) => r.counts.used ?? 0, cls: 'r' },
              { label: 'Filled', get: (r) => num(r.counts.filled ?? 0), sort: (r) => r.counts.filled ?? 0, cls: 'r' },
              { label: 'Loaned', get: (r) => num(r.counts.loaned ?? 0), sort: (r) => r.counts.loaned ?? 0, cls: 'r' },
              { label: 'Deposited', get: (r) => num(r.counts.deposited ?? 0), sort: (r) => r.counts.deposited ?? 0, cls: 'r' },
              { label: 'Most', get: (r) => Object.entries(r.items).sort((x, y) => y[1] - x[1]).slice(0, 3).map(([k, v]) => `${k} ×${num(v)}`).join(', ') || '—' },
              { label: 'Last', get: (r) => when(r.last), sort: (r) => r.last },
            ],
            { initial: 1, remember: 'ops-armory-members', empty: 'Nothing in these days.' },
          ),
          h('h3', {}, 'Every line'),
          table(
            list,
            [
              { label: 'When', get: (l) => when(l.at), sort: (l) => l.at },
              { label: 'Who', get: (l) => who(l.who, names), sort: (l) => whoText(l.who, names).toLowerCase() },
              { label: 'What', get: (l) => ACTION_LABEL[l.action], sort: (l) => l.action },
              { label: 'Item', get: (l) => (l.item ? `${l.item}${l.count > 1 ? ` ×${num(l.count)}` : ''}` : h('span', { class: 'muted small' }, l.text)), sort: (l) => l.item ?? '' },
              { label: 'To / from', get: (l) => (l.to ? who(l.to, names) : ''), sort: (l) => whoText(l.to, names) },
            ],
            { initial: 0, remember: 'ops-armory-lines', empty: 'Nothing in these days.', csv: false },
          ),
        );
      };
      const tools = h(
        'div',
        { class: 'toolbar' },
        filterSelect('What', [['', 'Anything'], ...ARMORY_ACTIONS.map((x) => [x, ACTION_LABEL[x]] as const)], action, (v) => ((action = v), draw()), 'armory-action'),
        filterSelect('Member', [['', 'Every member'], ...members], member, (v) => ((member = v), draw()), 'armory-member'),
        filterSelect('Item', [['', 'Every item'], ...items.map((x) => [x, x] as const)], item, (v) => ((item = v), draw()), 'armory-item'),
        csvButton(
          () => 'armory-history.csv',
          () => ({ header: ['time (UTC)', 'who', 'who id', 'what', 'item', 'count', 'to/from', 'to/from id', 'line'], rows: lines.filter(pick).map((l) => [new Date(l.at * 1000).toISOString(), l.who?.name ?? '', l.who?.id ?? '', l.action, l.item ?? '', l.count, l.to?.name ?? '', l.to?.id ?? '', l.text]) }),
        ),
      );
      draw();
      holder.append(tools, out);
    }),
  );
}

// ---------------------------------------------------------------- chains

export function chainsSection(names: Map<number, string>): HTMLElement {
  return once('chains', () => {
    const report = h('div', { 'data-ops': 'chain-report' });
    const showReport = (id: number) => {
      report.replaceChildren(loading('Reading the chain’s report…'));
      server<{ report: ChainReport | null; problem: string | null; kept: boolean }>('GET', `/api/faction/ops/chain/${id}`)
        .then((r) => report.replaceChildren(r.report ? chainReport(r.report, names, r.kept) : h('p', { class: 'notice' }, r.problem ?? 'No report.')))
        .catch((err) => report.replaceChildren(problem(err)));
      report.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    };
    const list = opsPanel('chains', 'Chains', 'chains', (a, holder) => {
      const chains = a.chains ?? [];
      holder.append(
        table(
          chains,
          [
            { label: 'Started', get: (c) => h('span', { title: `${tct(c.start * 1000)} TCT` }, tct(c.start * 1000)), sort: (c) => c.start },
            { label: 'Hits', get: (c) => num(c.chain), sort: (c) => c.chain, cls: 'r' },
            { label: 'Respect', get: (c) => num(c.respect, 2), sort: (c) => c.respect, cls: 'r' },
            { label: 'Lasted', get: (c) => (c.end ? duration(c.end - c.start) : h('span', { class: 't-green' }, 'going on')), sort: (c) => (c.end || Date.now() / 1000) - c.start, cls: 'r' },
            { label: '', get: (c) => h('button', { type: 'button', class: 'btn-small', 'data-chain': String(c.id), onclick: () => showReport(c.id) }, 'Report') },
          ],
          { initial: 0, remember: 'ops-chains', empty: 'No chains in these days.' },
        ),
      );
    });
    return h('div', {}, list, report);
  });
}

function chainReport(r: ChainReport, names: Map<number, string>, kept: boolean): HTMLElement {
  const name = (id: number) => (id ? ext(profileUrl(id), names.get(id) ?? `#${id}`) : h('span', { class: 'muted' }, '(removed)'));
  const d = r.details;
  return box(
    `Chain of ${num(d.chain)} — ${tct(r.start * 1000)}`,
    [
      facts([
        ['Respect', num(d.respect, 2)],
        ['Lasted', r.end ? duration(r.end - r.start) : 'going on'],
        ['Members who hit', num(d.members)],
        ['Targets', num(d.targets)],
        ['Best hit', num(d.best, 2)],
        ['War hits', num(d.war)],
        ['Leave · mug · hosp', `${num(d.leave)} · ${num(d.mug)} · ${num(d.hospitalize)}`],
        ['Retaliations · overseas', `${num(d.retaliations)} · ${num(d.overseas)}`],
        ['Assists · draws · losses', `${num(d.assists)} · ${num(d.draws)} · ${num(d.losses)}`],
      ]),
      h('h3', {}, `Bonus hits (${r.bonuses.length})`),
      table(
        r.bonuses,
        [
          { label: 'Hit', get: (b) => `#${num(b.chain)}`, sort: (b) => b.chain, cls: 'r' },
          { label: 'By', get: (b) => name(b.attacker), sort: (b) => names.get(b.attacker) ?? '' },
          { label: 'On', get: (b) => ext(profileUrl(b.defender), `#${b.defender}`), sort: (b) => b.defender },
          { label: 'Respect', get: (b) => num(b.respect, 2), sort: (b) => b.respect, cls: 'r' },
        ],
        { initial: 0, desc: false, empty: 'No bonus hits.' },
      ),
      h('h3', {}, `Members (${r.attackers.length})`),
      table(
        r.attackers,
        [
          { label: 'Member', get: (x) => name(x.id), sort: (x) => (names.get(x.id) ?? '').toLowerCase() },
          { label: 'Hits', get: (x) => num(x.hits), sort: (x) => x.hits, cls: 'r' },
          { label: 'Respect', get: (x) => num(x.respect, 2), sort: (x) => x.respect, cls: 'r' },
          { label: 'Average', get: (x) => num(x.average, 2), sort: (x) => x.average, cls: 'r' },
          { label: 'Best', get: (x) => num(x.best, 2), sort: (x) => x.best, cls: 'r' },
          { label: 'War', get: (x) => num(x.war), sort: (x) => x.war, cls: 'r' },
          { label: 'Bonus', get: (x) => num(x.bonuses), sort: (x) => x.bonuses, cls: 'r' },
          { label: 'Assists', get: (x) => num(x.assists), sort: (x) => x.assists, cls: 'r' },
          { label: 'Losses', get: (x) => num(x.losses), sort: (x) => x.losses, cls: 'r' },
        ],
        { initial: 2, remember: 'ops-chain-members', csv: false },
      ),
      r.nonAttackers.length ? h('p', { class: 'small' }, h('b', {}, `Didn’t hit (${r.nonAttackers.length}): `), ...r.nonAttackers.flatMap((id, i) => [i ? ', ' : '', name(id)])) : null,
      h(
        'div',
        { class: 'toolbar' },
        csvButton(
          () => `chain-${r.id}.csv`,
          () => ({ header: ['member', 'id', 'hits', 'respect', 'average', 'best', 'war', 'bonuses', 'assists', 'retaliations', 'overseas', 'losses'], rows: r.attackers.map((x) => [names.get(x.id) ?? '', x.id, x.hits, x.respect, x.average, x.best, x.war, x.bonuses, x.assists, x.retaliations, x.overseas, x.losses]) }),
        ),
        ext(`https://www.torn.com/war.php?step=chainreport&chainID=${r.id}`, 'Torn’s own report'),
      ),
      h('p', { class: 'muted small' }, kept ? 'Kept by Omni-Torn for your faction.' : 'Read from Torn now (kept an hour).'),
    ],
    { id: 'chain-report' },
  );
}

// ---------------------------------------------------------------- bank

export function bankSection(names: Map<number, string>, me: { id: number; name: string }): HTMLElement {
  return once('bank', () => {
    const top = h('div', {}, loading('Reading the bank…'));
    const readBank = () =>
      server<FactionBankAnswer>('GET', '/api/faction/ops/bank')
        .then((b) => {
          top.replaceChildren(...bankTop(b, names, me));
        })
        .catch((err) => top.replaceChildren(problem(err)));
    void readBank();
    const moves = opsPanel('funds', 'Money moves', 'bank-moves', (a, holder) => {
      const lines = a.funds ?? [];
      let kind: 'cash' | 'points' = 'cash';
      let member = '';
      const out = h('div', {});
      const members = [...new Map(lines.filter((l) => l.who).map((l) => [String(l.who!.id || l.who!.name), whoText(l.who, names)])).entries()].sort((x, y) => x[1].localeCompare(y[1]));
      const pick = (l: FundsLine) => l.kind === kind && (!member || String(l.who?.id || l.who?.name) === member);
      const amount = (n: number) => (kind === 'points' ? `${num(n)} points` : money(n));
      const draw = () => {
        const list = lines.filter(pick);
        const per = fundsByMember(list, kind);
        out.replaceChildren(
          ...kids(a.ownOnly ? h('p', { class: 'muted small', 'data-ops': 'own-only' }, 'Your own moves (bankers — the leader, co-leader and members they allowed on Faction → Delegates — see everyone’s).') : null),
          h('h3', {}, 'By member'),
          table(
            per,
            [
              { label: 'Member', get: (r) => who(r.who, names), sort: (r) => whoText(r.who, names).toLowerCase() },
              { label: 'Deposited', get: (r) => amount(r.deposited), sort: (r) => r.deposited, cls: 'r' },
              { label: 'Given out', get: (r) => amount(r.given), sort: (r) => r.given, cls: 'r' },
              { label: 'Changed', get: (r) => (r.adjusted ? amount(r.adjusted) : '—'), sort: (r) => r.adjusted, cls: 'r' },
              { label: 'Net', get: (r) => h('span', { class: r.net < 0 ? 'st-red' : '' }, amount(r.net)), sort: (r) => r.net, cls: 'r' },
              { label: 'Last', get: (r) => when(r.last), sort: (r) => r.last },
            ],
            { initial: 1, remember: 'ops-funds-members', empty: 'No money moves in these days.' },
          ),
          h('h3', {}, 'Every move'),
          table(
            list,
            [
              { label: 'When', get: (l) => when(l.at), sort: (l) => l.at },
              { label: 'Member', get: (l) => who(l.who, names), sort: (l) => whoText(l.who, names).toLowerCase() },
              { label: 'What', get: (l) => ({ deposit: 'Deposited', give: 'Given out', adjust: 'Balance changed', other: 'Other' })[l.action], sort: (l) => l.action },
              { label: 'Amount', get: (l) => amount(l.amount), sort: (l) => l.amount, cls: 'r' },
              { label: 'By', get: (l) => (l.by ? who(l.by, names) : ''), sort: (l) => whoText(l.by, names) },
            ],
            { initial: 0, remember: 'ops-funds-lines', empty: 'No money moves in these days.', csv: false },
          ),
        );
      };
      holder.append(
        h(
          'div',
          { class: 'toolbar' },
          filterSelect('Money or points', [['cash', 'Money'], ['points', 'Points']], kind, (v) => ((kind = v === 'points' ? 'points' : 'cash'), draw()), 'funds-kind'),
          a.ownOnly ? null : filterSelect('Member', [['', 'Every member'], ...members], member, (v) => ((member = v), draw()), 'funds-member'),
          csvButton(
            () => 'faction-money.csv',
            () => ({ header: ['time (UTC)', 'member', 'member id', 'what', 'amount', 'kind', 'by', 'by id'], rows: lines.filter(pick).map((l) => [new Date(l.at * 1000).toISOString(), l.who?.name ?? '', l.who?.id ?? '', l.action, l.amount, l.kind, l.by?.name ?? '', l.by?.id ?? '']) }),
          ),
        ),
        out,
      );
      draw();
    });
    return h('div', {}, top, moves);
  });
}

function bankTop(b: FactionBankAnswer, names: Map<number, string>, me: { id: number }): Node[] {
  const out: Node[] = [];
  const mine = b.balances?.members.find((m) => m.id === me.id);
  out.push(
    box(
      'Your balance',
      [
        b.problem && !b.balances ? h('p', { class: 'notice' }, b.problem) : null,
        b.balances ? (mine ? facts([['Money', money(mine.money)], ['Points', num(mine.points)]]) : h('p', { class: 'muted' }, 'You’re not in the faction’s balance list.')) : null,
        h('p', { class: 'muted small' }, 'Ask the bankers in your faction’s Discord with /withdraw (it checks your balance and pings them).', b.balancesAt ? ` Balances read ${ago(b.balancesAt)}.` : ''),
      ],
      { id: 'bank-mine' },
    ),
  );
  const waiting = b.requests.filter((r) => r.status === 'pending');
  const giveNote = h('p', { class: 'muted small' }, 'Give on Torn opens Torn’s own Give to user form; with the Omni-Torn extension (or the Torn PDA script) a “Fill in” button there fills in who and how much when you click it — you check it and press Torn’s button. Then mark it sent in Discord.');
  out.push(
    box(
      b.banker ? `Vault requests waiting (${waiting.length})` : 'Your vault requests',
      [
        b.requestsProblem ? h('p', { class: 'notice' }, b.requestsProblem) : null,
        table(
          b.banker ? waiting : b.requests,
          [
            { label: 'Asked', get: (r) => when(r.createdAt), sort: (r) => r.createdAt },
            { label: 'Member', get: (r) => ext(profileUrl(r.tornId), names.get(r.tornId) ?? r.tornName ?? `#${r.tornId}`), sort: (r) => (r.tornName ?? '').toLowerCase() },
            { label: 'Amount', get: (r) => (r.kind === 'points' ? `${num(r.amount)} points` : money(r.amount)), sort: (r) => r.amount, cls: 'r' },
            { label: 'Balance', get: (r) => { const m = b.balances?.members.find((x) => x.id === r.tornId); return m ? (r.kind === 'points' ? num(m.points) : money(m.money)) : '—'; }, cls: 'r' },
            b.banker
              ? { label: '', get: (r) => h('a', { class: 'btn-small', href: bankGiveLink(r.tornId, r.amount, r.kind), target: '_blank', rel: 'noopener noreferrer', 'data-give': String(r.id) }, 'Give on Torn') }
              : { label: 'Status', get: (r) => ({ pending: 'Waiting', sent: r.newsCheck === 'seen' ? 'Sent (in the news)' : 'Sent', cancelled: 'Cancelled', expired: 'Timed out' })[r.status], sort: (r) => r.status },
            { label: 'Times out', get: (r) => (r.status === 'pending' && r.expiresAt ? h('span', { title: `${tct(r.expiresAt * 1000)} TCT` }, r.expiresAt * 1000 > Date.now() ? `in ${duration(r.expiresAt - Date.now() / 1000)}` : 'now') : '—'), sort: (r) => r.expiresAt ?? 0 },
          ],
          { initial: 0, empty: b.banker ? 'Nothing waiting.' : 'No requests in the last 30 days.', remember: 'ops-bank-requests' },
        ),
        b.banker ? giveNote : null,
      ],
      { id: 'bank-requests' },
    ),
  );
  if (b.banker && b.balances) {
    const bal = b.balances;
    out.push(
      box(
        'Balances',
        [
          bal.faction ? facts([['Faction money', money(bal.faction.money)], ['Faction points', num(bal.faction.points)], ['Members’ money', money(bal.members.reduce((s, m) => s + m.money, 0))]]) : null,
          table(
            bal.members,
            [
              { label: 'Member', get: (m) => ext(profileUrl(m.id), names.get(m.id) ?? m.name), sort: (m) => (names.get(m.id) ?? m.name).toLowerCase() },
              { label: 'Money', get: (m) => money(m.money), sort: (m) => m.money, cls: 'r' },
              { label: 'Points', get: (m) => num(m.points), sort: (m) => m.points, cls: 'r' },
              { label: '', get: (m) => (m.money > 0 ? h('a', { class: 'btn-small', href: bankGiveLink(m.id, m.money), target: '_blank', rel: 'noopener noreferrer', title: 'Torn’s Give form with their whole balance filled in' }, 'Give all on Torn') : '') },
            ],
            { initial: 1, remember: 'ops-bank-balances', csv: false },
          ),
          h('div', { class: 'toolbar' }, csvButton(() => 'faction-balances.csv', () => ({ header: ['member', 'id', 'money', 'points'], rows: bal.members.map((m) => [m.name, m.id, m.money, m.points]) }))),
        ],
        { id: 'bank-balances' },
      ),
    );
  }
  return out;
}

// ---------------------------------------------------------------- raids & territory

export function raidsSection(factionId: number): HTMLElement {
  return once('raids', () =>
    opsPanel('raids', 'Raids & territory', 'raids', (a, holder) => {
      const wars = a.raids ?? [];
      const held = a.territory ?? [];
      const side = (w: (typeof wars)[number], ours: boolean) => w.factions.find((f) => (f.id === factionId) === ours);
      const enemy = (w: (typeof wars)[number]) => { const f = side(w, false); return f ? ext(`https://www.torn.com/factions.php?step=profile&ID=${f.id}`, f.name) : '—'; };
      const score = (w: (typeof wars)[number]) => `${num(side(w, true)?.score ?? 0)} – ${num(side(w, false)?.score ?? 0)}${w.target ? ` (of ${num(w.target)})` : ''}`;
      const result = (w: (typeof wars)[number]) => (!w.end ? h('span', { class: 't-green' }, 'going on') : w.winner ? (w.winner === factionId ? 'won' : 'lost') : (w.result ?? 'ended'));
      holder.append(
        h('h3', {}, `Territory held (${held.length})`),
        ...kids(held.length ? facts([['Respect a day', num(held.reduce((s, t) => s + t.respect, 0), 2)], ['Rackets', String(held.filter((t) => t.racket).length)]]) : null),
        table(
          held,
          [
            { label: 'Territory', get: (t) => t.id, sort: (t) => t.id },
            { label: 'Sector', get: (t) => num(t.sector), sort: (t) => t.sector, cls: 'r' },
            { label: 'Respect a day', get: (t) => num(t.respect, 2), sort: (t) => t.respect, cls: 'r' },
            { label: 'Racket', get: (t) => (t.racket ? `${t.racket.name}${t.racket.level ? ` (level ${t.racket.level})` : ''}${t.racket.reward ? ` · ${t.racket.reward}` : ''}` : '—'), sort: (t) => t.racket?.name ?? '' },
            { label: 'Held since', get: (t) => (t.acquiredAt ? when(t.acquiredAt) : '—'), sort: (t) => t.acquiredAt },
          ],
          { initial: 2, remember: 'ops-territory', empty: 'No territory.' },
        ),
        h('h3', {}, `Raids and territory wars (${wars.length})`),
        table(
          wars,
          [
            { label: 'Started', get: (w) => h('span', { title: `${tct(w.start * 1000)} TCT` }, tct(w.start * 1000)), sort: (w) => w.start },
            { label: 'Kind', get: (w) => (w.kind === 'raid' ? 'Raid' : `Territory war${w.territory ? ` (${w.territory})` : ''}`), sort: (w) => w.kind },
            { label: 'We', get: (w) => (side(w, true)?.aggressor ? 'attack' : 'defend'), sort: (w) => Number(side(w, true)?.aggressor) },
            { label: 'Against', get: enemy, sort: (w) => side(w, false)?.name ?? '' },
            { label: 'Score', get: score, cls: 'r' },
            { label: 'Result', get: result, sort: (w) => w.end || 9e15 },
          ],
          { initial: 0, remember: 'ops-raids', empty: 'No raids or territory wars in these days.' },
        ),
      );
    }),
  );
}
